import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild} from '@angular/core';
import { ProtocolComponent } from '../protocol-configuration/dto/ProtocolComponent';
import { ActionComponent } from '../protocol-configuration/dto/ActionComponent';
import { ProcessStep } from '../models-new/processStep';
import { GenericPlateUpdateDTO } from '../dto/genericPlateUpdateDTO';
import { ButtonType } from '../constants/ButtonType';
import { ArrayStatus, StepStatus } from '../constants/Status';
import { ActionComponentType } from '../constants/ActionComponentType';
import { MessagesNewService } from '../messages-new/messages.service';
import { ProtocolComponentName } from '../constants/ProtocolComponentMapping';
import { Action } from '../models-new/action';
import { ProtocolStepConfig } from './protocol-step-config';
import { OpenDialogDTO } from '../multiplate-update/open-dialog-dto';
import { EVALUATION_SAMPLE_RERUN, InboundFileUploadComponentDTO } from '../inbound-file-configuration/model/inboundfile';
import { StorageService } from '../storage-new/storage-new.service';

@Component({
	selector: 'app-protocol-step',
	templateUrl: './protocol-step.component.html',
	styleUrls: ['./protocol-step.component.css']
})
export class ProtocolStepComponent implements OnChanges,OnInit {
	@Input() protocolStepName: string = "";
	@Input() protocolStepMap: ProtocolComponent = new ProtocolComponent();
	@Input() plateProcessStep: ProcessStep = new ProcessStep();
	@Input() plateProcessStepMap: Map<string, ProcessStep> = new Map();
	@Input() processStepActionMap: Map<string, Action> = new Map();
	@Input() isProcessStepActive: boolean = false;
	@Input() extraConfigs: ProtocolStepConfig = new ProtocolStepConfig();

	@Output() updatePlateEmitter = new EventEmitter<any>();
	@Output() openButtonDialogEmitter = new EventEmitter<OpenDialogDTO>();
	@Output() onWorkFileDownloadClicked = new EventEmitter<string>();
	@Output() onStaticLynxFileSelected = new EventEmitter<File>();
	@Output() onInboundFileUploadClicked = new EventEmitter<InboundFileUploadComponentDTO>();
	
	blocked: boolean = false;
	isArray: boolean = false;
	sendStatusDeliveredOnCompleteStep = false
	isLastProtocolComponentBeforeResultHandling: boolean = false;
	isLynxWorkFileSelected = false;
	selectedLynxWorkFile:File = undefined;
	@ViewChild("lynxWorkFileUpload") lynxWorkFileUpload:ElementRef;
	isUploadFileSelected = false;
	selectedUploadFile: File = undefined;
	@ViewChild("inboundFileUpload") inboundFileUpload: ElementRef;
	
	readonly RESTORATION_FAILED_SUMMARY = "Restoration failed";
	readonly ACTION_BUTTON_CLASS = "action-button";
	readonly JUSTIFY_CONTENT_BETWEEN = "justify-content-between";
	readonly JUSTIFY_CONTENT_CENTER = "justify-content-center";
    requiredActions:string[] = [];


	constructor(private readonly messagesService: MessagesNewService, private readonly storageService: StorageService){}

	ngOnInit() {
		// Do not remove this, this is used for refreshing the active action timer
		setInterval(() => {}, 1000);
        this.filterActions();
	}

    filterActions(): void {
        this.requiredActions = [];
        this.protocolStepMap.actionList.forEach(actionName => {
            const action = this.protocolStepMap.actionMap[actionName];
			if(action.actionType === ActionComponentType.WORK_FILE_UPLOAD && action.inboundFileDefinitionConfigured === EVALUATION_SAMPLE_RERUN){
				return;
			}
            if(action.actionType !== ActionComponentType.WORK_FILE_DOWNLOAD){
                this.requiredActions.push(actionName);
            }
        });
    }

	ngOnChanges(){
		this.isArray = this.extraConfigs.isArray;
		this.isLastProtocolComponentBeforeResultHandling = this.extraConfigs.isLastProtocolComponentBeforeResultHandling;
		this.sendStatusDeliveredOnCompleteStep = this.extraConfigs.isLastStepOfArrayList;
	}

	removePlateFromStorage() {
		const body = {
			"labid": this.extraConfigs.labID,
			"plateid": this.extraConfigs.plateID,
		}
		this.blocked = true
		this.storageService.removePlateFromStorage(body).subscribe({
			next: (result) => {
				this.blocked = false
				this.messagesService.addSuccessMessage({
					severity: "success", summary: "Restoration success",
					detail: result
				})
			},
			error: (error: any) => {
				this.blocked = false
				this.messagesService.addErrorMessage({
					severity: "error", summary: this.RESTORATION_FAILED_SUMMARY,
					detail: error
				})
			}
		})
	}

	startButtonClick() {
		let actionComponent: ActionComponent = this.protocolStepMap.start;
		if (!actionComponent.hasPopUp) {
			if(!this.isProcessStepActive || !this.isButtonActive(ButtonType.START)){
				this.messagesService.addWarningMessage({
					severity:'warn', summary: 'Invalid Action!',
					detail: "This button does not have a pop-up configured. Please select a plate to continue with this action!"
				})
				return;
			}

			this.updatePlate(ButtonType.START);
			return;
		}
		this.openButtonDialog(ButtonType.START, "", 0, "start-button", actionComponent.actionType);
	}

	endButtonClick() {
		let actionComponent: ActionComponent = this.protocolStepMap.end;
		if (!actionComponent.hasPopUp) {
			if(!this.isProcessStepActive || !this.isButtonActive(ButtonType.END)){
				this.messagesService.addWarningMessage({
					severity:'warn', summary: 'Invalid Action!',
					detail: "This button does not have a pop-up configured. Please select a plate to continue with this action!"
				})
				return;
			}

			this.updatePlate(ButtonType.END);
			return;
		}
		this.openButtonDialog(ButtonType.END, "", 0, "complete-button", actionComponent.actionType);
	}

	actionButtonClick(actionName: string, actionIndex: number) {
		let actionComponent: ActionComponent = this.protocolStepMap.actionMap[actionName];
        const requiredActionIndex = this.requiredActions.indexOf(actionName);
		if (!actionComponent.hasPopUp) {
			if(actionComponent.actionType === ActionComponentType.WORK_FILE_DOWNLOAD){
				this.onWorkFileDownloadClicked.emit(actionComponent.workFileDefinitionConfigured);
                return;
			}
			if(!this.isProcessStepActive || !this.isButtonActive(ButtonType.ACTION, actionName)){
				if(actionComponent.actionType !== ActionComponentType.WORK_FILE_DOWNLOAD)
					this.messagesService.addWarningMessage({
						severity:'warn', summary: 'Invalid Action!',
						detail: "This button does not have a pop-up configured. Please select a plate to continue with this action!"
					})
				return;
			}

			this.updatePlate(ButtonType.ACTION, requiredActionIndex);
			return;
		}
		this.openButtonDialog(ButtonType.ACTION, actionName, requiredActionIndex, this.ACTION_BUTTON_CLASS, actionComponent.actionType);
	}

	qcPassButtonClick(actionName: string, actionIndex: number) {
		let actionComponent: ActionComponent = this.protocolStepMap.actionMap[actionName];
        const requiredActionIndex = this.requiredActions.indexOf(actionName);
		if (!actionComponent.hasPopUp) {
			if(!this.isProcessStepActive || !this.isButtonActive(ButtonType.ACTION, actionName)){
				this.messagesService.addWarningMessage({
					severity:'warn', summary: 'Invalid Action!',
					detail: "This button does not have a pop-up configured. Please select a plate to continue with this action!"
				})
				return;
			}

			this.updatePlate(ButtonType.ACTION, requiredActionIndex);
			return;
		}
		this.openButtonDialog(ButtonType.ACTION, actionName, requiredActionIndex, this.ACTION_BUTTON_CLASS, actionComponent.actionType);
	}

	qcFailButtonClick(actionName: string, actionIndex: number) {
		let actionComponent: ActionComponent = this.protocolStepMap.actionMap[actionName];
        const requiredActionIndex = this.requiredActions.indexOf(actionName);
		if (!actionComponent.hasPopUp) {
			this.messagesService.addWarningMessage({
				severity:'warn', summary: 'Invalid Action!',
				detail: "This button does not have a pop-up configured. Please configure redo popups for this qc fail button!"
			})
			return;
		}
		this.openButtonDialog(ButtonType.ACTION, actionName, requiredActionIndex, "complete-button", actionComponent.actionType, true);
	}

	openButtonDialog(buttonType: ButtonType, actionName: string, actionIndex: number, buttonClass: string, actionType: string, qcFail = false) {
		const openDialogDTO =  new OpenDialogDTO()
		openDialogDTO.protocolStepName = this.protocolStepName;
		openDialogDTO.buttonType = buttonType;
		openDialogDTO.actionName = actionName;
		openDialogDTO.actionIndex = actionIndex;
		openDialogDTO.actionType = actionType;
		openDialogDTO.buttonClass = buttonClass;
		openDialogDTO.qcFail = qcFail;
        openDialogDTO.requiredActions = this.requiredActions;

		this.openButtonDialogEmitter.emit(openDialogDTO);
	}

	isActionCompleted(actionName: string) {
		return this.processStepActionMap.has(actionName);
	}

	showLargeButton(actionName: string): boolean{
		let actionType = this.protocolStepMap.actionMap[actionName].actionType;
		if(actionType == ActionComponentType.QC || this.protocolStepMap.actionMap[actionName].smallButton)
			return false

		if(actionType == ActionComponentType.NORMAL)
			return true

		if(actionType == ActionComponentType.TIMER && !this.isActionCompleted(actionName))
			return true

		if(actionType === ActionComponentType.WORK_FILE_DOWNLOAD){
			return true;
		}

		return false
	}

	showStopAction(actionName){
		return this.protocolStepMap.actionMap[actionName].actionType==ActionComponentType.STOP && this.processStepActionMap.has(actionName.substring(5))
	}

	isActionWithTimer(actionIndex: number) {
		return this.protocolStepMap.actionMap[this.protocolStepMap.actionList[actionIndex]].actionType == ActionComponentType.TIMER
	}

	getStartButtonClass() {
		return this.plateProcessStep.startTimeStamp ? this.JUSTIFY_CONTENT_BETWEEN : `${this.JUSTIFY_CONTENT_CENTER} ${this.getActiveButtonClass(ButtonType.START)}`;
	}

	getEndButtonClass() {
		return this.plateProcessStep.endTimeStamp ? this.JUSTIFY_CONTENT_BETWEEN : `${this.JUSTIFY_CONTENT_CENTER} ${this.getActiveButtonClass(ButtonType.END)}`;
	}


	getActionButtonClass(actionName: string) {
		if (this.isActionCompleted(actionName)) {
			return this.JUSTIFY_CONTENT_BETWEEN
		}
		return `${this.JUSTIFY_CONTENT_CENTER} ${this.getActiveButtonClass(ButtonType.ACTION, actionName)}`;
	}

	getStopActionButtonClass(actionName: string) {
		if (this.isActionCompleted(actionName)) {
			return `${this.JUSTIFY_CONTENT_BETWEEN} ${this.ACTION_BUTTON_CLASS} text-primary`;
		}
		return `${this.JUSTIFY_CONTENT_CENTER} stop-action-button text-white ${this.getActiveButtonClass(ButtonType.ACTION, actionName, "-white")}`
	}

	getQCFailActionButtonClass(actionName: string) {
		return	`${this.JUSTIFY_CONTENT_CENTER} ${this.getActiveButtonClass(ButtonType.ACTION, actionName, "-white")}`
	}

	getActiveButtonClass(buttonType: string, actionName: string = "", classSuffix: string = "") {
		let activeButtonClass = "div-button-active"+classSuffix;

		if (ButtonType.ACTION == buttonType) {
			let actionComponent = this.protocolStepMap.actionMap[actionName];
            if(actionComponent.actionType === ActionComponentType.WORK_FILE_DOWNLOAD){
                return "";
            }
			activeButtonClass = actionComponent.smallButton || actionComponent.actionType == ActionComponentType.QC ? "div-small-button-active"+classSuffix : "div-button-active"+classSuffix
		}

		return this.isButtonActive(buttonType, actionName) ? activeButtonClass : "";
	}

	isButtonActive(buttonType: string, actionName: string = ""): boolean {
		if (!this.isProcessStepActive)
			return false;

		if (buttonType == ButtonType.START && !this.plateProcessStep.startTimeStamp && !this.plateProcessStep.startPUID && this.protocolStepName!=ProtocolComponentName.RECOMBINE_DNA)
			return true;

		if(buttonType == ButtonType.START && this.protocolStepName == ProtocolComponentName.RECOMBINE_DNA){
			let wga = this.plateProcessStepMap.get(ProtocolComponentName.AMPLIFY_WGA);
			let pgx = this.plateProcessStepMap.get(ProtocolComponentName.AMPLIFY_PGX);
			if(wga?.endPUID.trim() && pgx?.endPUID.trim()){
				return true;
			}
		}

		if(buttonType == ButtonType.ACTION && this.isActionButtonActive(actionName))
			return true;

		if (buttonType == ButtonType.END && this.isEndButtonActive())
			return true;

		return false;
	}

	isEndButtonActive(){
		if(!this.plateProcessStep.startTimeStamp && !this.plateProcessStep.startPUID)
			return false;

		if (this.checkActionsForCompleteStep())
			return false;

		if (this.plateProcessStep.endTimeStamp || this.plateProcessStep.endPUID)
			return false;

		return true;
	}

	private checkActionsForCompleteStep(): boolean {
		return this.requiredActions.length !== this.countUniqueFileUploadActions(this.plateProcessStep.action)
	}

	countUniqueFileUploadActions(actionList: Action[]): number {
		return new Set(actionList.map(action => action.action)).size;
	}

	isActionButtonActive(actionName: string = ""){
		if (!this.plateProcessStep.startTimeStamp || this.processStepActionMap.has(actionName))
			return false;

		const actionIndex = this.requiredActions.indexOf(actionName);
		if(actionIndex>0 && !this.processStepActionMap.has(this.requiredActions[actionIndex-1]))
			return false;

		return true;
	}

	updatePlate(buttonType: string, actionIndex: number = 0) {
		let dto = new GenericPlateUpdateDTO();
		if (this.extraConfigs.isStoragePlate) {
			this.removePlateFromStorage();
			dto.storagePlate = false;
		}
		dto.updateType = buttonType;
		dto.processStepName = this.protocolStepName;
		if (ButtonType.ACTION == buttonType) {
			dto.actionName = this.requiredActions[actionIndex]
			dto.actionType = this.protocolStepMap.actionMap[dto.actionName].actionType
			dto.runtime = this.protocolStepMap.actionMap[dto.actionName].timerLen
		}
		else {
			this.updateDTOWithNewPlateStatus(dto, buttonType)
		}


		this.updatePlateEmitter.emit(dto);
	}

	private updateDTOWithNewPlateStatus(dto: GenericPlateUpdateDTO, buttonType: string) {
		if(buttonType === ButtonType.END && this.sendStatusDeliveredOnCompleteStep){
			dto.status = ArrayStatus.DELIVERED
		}else if (this.isLastProtocolComponentBeforeResultHandling && buttonType === ButtonType.END) {
			dto.status = ArrayStatus.WAITING_FOR_ANALYSIS
		} else if (this.isArray) {
			dto.status = StepStatus.arrayStepStatusMap.get(this.protocolStepName + "_" + buttonType) || "";
		} else if((this.protocolStepName === ProtocolComponentName.AMPLIFY_WGA || this.protocolStepName === ProtocolComponentName.AMPLIFY_PGX) && buttonType===ButtonType.END) {
			this.prepareStatusForWGAPGXEnd(dto, buttonType);
		} else {
			dto.status = StepStatus.plateStepStatusMap.get(this.protocolStepName + "_" + buttonType) || "";
		}
	}

	private prepareStatusForWGAPGXEnd(dto: GenericPlateUpdateDTO, buttonType: ButtonType) {
		if (this.protocolStepName == ProtocolComponentName.AMPLIFY_WGA && this.plateProcessStepMap.get(ProtocolComponentName.AMPLIFY_PGX)?.endPUID?.trim()) {
			dto.status = StepStatus.plateStepStatusMap.get(ProtocolComponentName.PRECELL + "_" + buttonType);
		} else if (this.protocolStepName == ProtocolComponentName.AMPLIFY_PGX && this.plateProcessStepMap.get(ProtocolComponentName.AMPLIFY_WGA)?.endPUID?.trim()) {
			dto.status = StepStatus.plateStepStatusMap.get(ProtocolComponentName.PRECELL + "_" + buttonType);
		} else {
			dto.status = StepStatus.plateStepStatusMap.get(this.protocolStepName + "_" + buttonType) || "";
		}
	}

	getLocalDate(dateInUTC: Date){
		if(!dateInUTC || dateInUTC==null) return;
		let offset = new Date().getTimezoneOffset();
		let dateInLocal = new Date(dateInUTC);
		dateInLocal.setMinutes(dateInLocal.getMinutes()-offset);
		return dateInLocal;
	}

	calculateRunningTimeForAction(actionName: string) {
		if (!this.processStepActionMap.has(actionName)) {
			return;
		}

		let actionStartTime: Date = this.getLocalDate(this.processStepActionMap.get(actionName).actionStartTime);
		if (this.protocolStepMap.actionMap[actionName].actionType == ActionComponentType.TIMER) {
			return this.calculatingTimes(this.calculateRunningTime(actionStartTime));
		}
		if (this.protocolStepMap.actionMap[actionName].actionType == ActionComponentType.STOP) {

			return this.calculatingTimes(this.calculateTotalTime(this.getLocalDate(this.processStepActionMap.get(actionName.substring(5)).actionStartTime), actionStartTime));
		}
	}

	calculateRunningTime(actionStartTime: Date) {
		let currentDateLocal = new Date();
		return (currentDateLocal.getTime() - actionStartTime.getTime()) / 1000;
	}

	calculateTotalTime(actionStartTime: Date, actionEndTime: Date) {
		return (actionEndTime.getTime() - actionStartTime.getTime()) / 1000;
	}

	calculatingTimes(timeLeftInSecs: number) {
		let timeLeft = Math.floor(timeLeftInSecs);
		if (timeLeft == 0)
			return "00:00:00";
		let sec = 0, min = 0, hr = 0
		if (timeLeft % 60 != 0) {
			sec = timeLeft % 60;
			timeLeft = timeLeft - sec;
		}
		timeLeft = timeLeft / 60;
		if (timeLeft % 60 != 0) {
			min = timeLeft % 60;
			timeLeft = timeLeft - min;
		}
		timeLeft = timeLeft / 60;
		hr = timeLeft;
		let secStr = "00" + sec, minStr = "00" + min, hrStr = "00" + hr
		return hrStr.substring(hrStr.length - 2) + ':' + minStr.substring(minStr.length - 2) + ":" + secStr.substring(secStr.length - 2);
	}

	getActionPUID(actionName: string){
		return this.processStepActionMap.get(actionName).actionPUID;
	}

	getActionTimeStamp(actionName: string){
		return this.processStepActionMap.get(actionName).actionStartTime;
	}

	onInboundFileSelected(event: any): void{
		const files = event.target.files
		if(files.length === 0){
			this.messagesService.addWarningMessage({
				severity: 'warn',
				summary: 'No Work file selected!!!'
			});
			this.isUploadFileSelected = false;
			return;
		}
		if (files.length > 1) {
			this.messagesService.addWarningMessage({
				severity: 'warn',
				summary: 'Multiple files selected. Please select only one file'
			});
			return;
		}
		this.selectedUploadFile = files[0];
		this.isUploadFileSelected = true;
	}

	onInboundFileUpload(action: ActionComponent): void{
		if(!this.selectedUploadFile){
			this.messagesService.addWarningMessage({
			  severity:'warn',
			  summary:'No file selected. Please upload a file and try again'
			});
			this.inboundFileUpload.nativeElement.value = "";
			return;
		}
		this.onInboundFileUploadClicked.emit(
			{
				file: this.selectedUploadFile,
				inboundFileDefinitionName: action.inboundFileDefinitionConfigured,
				stepName: this.protocolStepName,
				actionName: action.name
			}
		);
    	this.inboundFileUpload.nativeElement.value = "";
		this.selectedUploadFile = null;
		this.isUploadFileSelected = false;
	}

	onLynxFileSelected(event: any): void {
		const files = event.target.files;
		if (files.length === 0) {
			this.messagesService.addWarningMessage({
				severity: 'warn',
				summary: 'No Lynx file selected!!!'
			});
			this.isLynxWorkFileSelected = false;
			return;
		}
		if (files.length > 1) {
			this.messagesService.addWarningMessage({
				severity: 'warn',
				summary: 'Multiple files selected. Please select only one file'
			});
			return;
		}
		this.selectedLynxWorkFile = files[0];
		this.isLynxWorkFileSelected = true;
	}

	onLynxFileUpload(): void {
		if(!this.selectedLynxWorkFile){
		this.messagesService.addWarningMessage({
			severity:'warn',
			summary:'No lynx file selected. Please select one lynx upload and try again'
		});
		this.lynxWorkFileUpload.nativeElement.value = "";
		return;
		}
		this.onStaticLynxFileSelected.emit(this.selectedLynxWorkFile);
    	this.lynxWorkFileUpload.nativeElement.value = "";
		this.isLynxWorkFileSelected = false;
	}
}
